'use client';

import { Dispatch, FC, SetStateAction, useState } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks';
import { GreenCtaLink } from '@/components/common/GreenCtaLink';
import CaseIndustryShowcaseRedesign from './variants/Redesign';

type IndustryItem = {
  industry_name?: string;
  badge_label?: string;
  background_image?: import('@prismicio/client').ImageField;
  brand_logo?: import('@prismicio/client').ImageField;
  brand_name?: string;
  stat_headline?: string;
  description?: import('@prismicio/client').RichTextField;
  cta_label?: string;
  cta_link?: import('@prismicio/client').LinkField;
};

type CaseIndustryShowcasePrimary = {
  industries?: IndustryItem[];
  kicker?: string;
  title?: import('@prismicio/client').RichTextField;
  see_all_label?: string;
  see_all_link?: import('@prismicio/client').LinkField;
};

type CaseIndustryShowcaseSlice = {
  slice_type: string;
  variation: string;
  primary: CaseIndustryShowcasePrimary;
};

type CarouselProps = {
  industries: IndustryItem[];
  activeIndex: number;
  setActiveIndex: Dispatch<SetStateAction<number>>;
};

/**
 * Slider-style carousel — visually matched to
 * IndustryTestimonialsCarousel/with_case_study_slider: tall pill cards
 * (rounded-[100px]) that expand on select, with an expanded right panel
 * (label, logo, stat headline, description, CTA + prev/next).
 */
function SliderCarousel({
  industries,
  activeIndex,
  setActiveIndex,
}: CarouselProps) {
  const isMobileCards = useIsMobile({ width: 1023 });
  const total = industries.length;
  const goPrev = () => setActiveIndex((i) => (i - 1 + total) % total);
  const goNext = () => setActiveIndex((i) => (i + 1) % total);

  return (
    <>
      {/* ── Desktop Cards Row ── */}
      {!isMobileCards && (
        <div className="relative z-[1] hidden gap-[8px] lg:flex">
          {industries.map((item, index) => {
            const isActive = index === activeIndex;
            const cardBg = item.background_image;

            return (
              <div
                key={index}
                role="button"
                tabIndex={0}
                aria-expanded={isActive}
                aria-label={item.industry_name ?? `Card ${index + 1}`}
                onClick={() => !isActive && setActiveIndex(index)}
                onKeyDown={(e) => {
                  if (!isActive && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setActiveIndex(index);
                  }
                }}
                className={cn(
                  'relative h-[550px] overflow-hidden rounded-[100px] transition-all duration-700 ease-in-out',
                  isActive
                    ? 'flex-[1_1_0%] min-w-0'
                    : 'flex-[0_0_136px] cursor-pointer hover:flex-[0_0_142px]',
                )}
              >
                {/* Card background image */}
                {isFilled.image(cardBg) && (
                  <PrismicNextImage
                    field={cardBg}
                    className="absolute inset-0 size-full rounded-[100px] object-cover"
                    fallbackAlt=""
                  />
                )}

                {/* Blur overlay — visible when collapsed, transparent when active */}
                <div
                  className={cn(
                    'absolute inset-0 rounded-[100px] transition-all duration-700',
                    isActive
                      ? 'bg-transparent backdrop-blur-0'
                      : 'bg-[rgba(5,29,25,0.7)] backdrop-blur-[21px]',
                  )}
                />

                {/* ── Expanded content (right panel) ── */}
                <div
                  className={cn(
                    'absolute inset-y-0 right-0 flex flex-col justify-between overflow-hidden rounded-r-[100px] bg-black/60 backdrop-blur-[7.5px] transition-all duration-700',
                    isActive
                      ? 'w-[581px] px-[100px] py-[60px] opacity-100'
                      : 'w-0 px-0 py-[60px] opacity-0 pointer-events-none',
                  )}
                >
                  {/* Top: industry label */}
                  <div className="flex shrink-0 items-center gap-[24px]">
                    <span className="whitespace-nowrap text-[18px] font-normal uppercase leading-[1.2] text-white">
                      {item.industry_name}
                    </span>
                    <span className="size-[12px] shrink-0 rounded-full border-2 border-white" />
                  </div>

                  {/* Bottom: logo + headline + description + cta + nav */}
                  <div className="flex flex-col gap-[24px]">
                    {isFilled.image(item.brand_logo) && (
                      <PrismicNextImage
                        field={item.brand_logo}
                        className="h-[38px] w-auto self-start object-contain"
                        fallbackAlt=""
                      />
                    )}

                    {item.stat_headline && (
                      <p className="text-[28px] font-bold leading-[1.2] text-white">
                        {item.stat_headline}
                      </p>
                    )}

                    {isFilled.richText(item.description) && (
                      <PrismicRichText
                        field={item.description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-[15px] font-light leading-[1.5] text-white/85">
                              {children}
                            </p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold">
                              {children}
                            </strong>
                          ),
                        }}
                      />
                    )}

                    <div className="flex items-center gap-[24px]">
                      {isFilled.link(item.cta_link) && (
                        <PrismicNextLink
                          field={item.cta_link}
                          className="shrink-0 whitespace-nowrap rounded-full bg-[#00f557] px-[24px] py-[12px] text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90"
                        >
                          {item.cta_label || 'View case study'}
                        </PrismicNextLink>
                      )}

                      {/* Progress line */}
                      <div className="flex flex-1 items-center">
                        <div className="h-px w-[36px] bg-white" />
                        <div className="h-px flex-1 bg-white/30" />
                      </div>

                      {/* Prev / Next */}
                      <div className="flex items-center gap-[8px]">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            goPrev();
                          }}
                          className="flex size-[36px] items-center justify-center text-white transition-opacity hover:opacity-70"
                          aria-label="Previous"
                        >
                          <ChevronLeft className="size-5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            goNext();
                          }}
                          className="flex size-[36px] items-center justify-center text-white transition-opacity hover:opacity-70"
                          aria-label="Next"
                        >
                          <ChevronRight className="size-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Collapsed content (vertical label + expand icon) ── */}
                <div
                  className={cn(
                    'absolute inset-0 flex flex-col items-center justify-between py-[60px] transition-opacity duration-500',
                    isActive ? 'pointer-events-none opacity-0' : 'opacity-100',
                  )}
                >
                  {/* Rotated industry label */}
                  <div
                    className="flex flex-1 items-center justify-center"
                    style={{ containerType: 'size' }}
                  >
                    <div className="-rotate-90 flex w-[100cqh] flex-none items-center justify-end gap-[24px] whitespace-nowrap pr-[5px]">
                      <span className="text-[18px] font-normal uppercase leading-[1.2] text-white">
                        {item.industry_name}
                      </span>
                      <span className="size-[12px] shrink-0 rounded-full border-2 border-white" />
                    </div>
                  </div>

                  {/* Expand indicator */}
                  <span className="flex size-[36px] items-center justify-center rounded-full border border-white/50">
                    <Plus className="size-5 text-white" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Mobile Cards (always expanded — no accordion) ── */}
      {isMobileCards && (
        <div className="relative z-[1] flex flex-col gap-[16px] lg:hidden">
          {industries.map((item, index) => {
            const cardBg = item.background_image;

            return (
              <div
                key={index}
                className="relative min-h-[420px] overflow-hidden rounded-[24px]"
              >
                {isFilled.image(cardBg) && (
                  <PrismicNextImage
                    field={cardBg}
                    className="absolute inset-0 size-full object-cover"
                    fallbackAlt=""
                  />
                )}
                <div className="absolute inset-0 bg-black/60 backdrop-blur-[7.5px]" />

                <div className="relative flex min-h-[420px] flex-col justify-between gap-10 p-[24px]">
                  <div className="flex items-center gap-[12px]">
                    <span className="text-[16px] font-normal uppercase leading-[1.2] text-white">
                      {item.industry_name}
                    </span>
                    <span className="size-[10px] shrink-0 rounded-full border-2 border-white" />
                  </div>

                  <div className="flex flex-col gap-[20px]">
                    {isFilled.image(item.brand_logo) && (
                      <PrismicNextImage
                        field={item.brand_logo}
                        className="h-[36px] w-auto self-start object-contain"
                        fallbackAlt=""
                      />
                    )}

                    {item.stat_headline && (
                      <p className="text-[26px] font-bold leading-[1.2] text-white">
                        {item.stat_headline}
                      </p>
                    )}

                    {isFilled.richText(item.description) && (
                      <PrismicRichText
                        field={item.description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-[15px] font-light leading-[1.5] text-white/85">
                              {children}
                            </p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold">
                              {children}
                            </strong>
                          ),
                        }}
                      />
                    )}

                    {isFilled.link(item.cta_link) && (
                      <PrismicNextLink
                        field={item.cta_link}
                        className="w-fit rounded-full bg-[#00f557] px-[24px] py-[12px] text-[15px] font-bold leading-[1.5] text-[#002726]"
                      >
                        {item.cta_label || 'View case study'}
                      </PrismicNextLink>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/**
 * Original collapsed-pill carousel used by the `default` variation.
 */
function IndustryCarousel({
  industries,
  activeIndex,
  setActiveIndex,
}: CarouselProps) {
  const total = industries.length;
  const handlePrev = () => setActiveIndex((i) => (i - 1 + total) % total);
  const handleNext = () => setActiveIndex((i) => (i + 1) % total);

  return (
    <div className="flex gap-3 md:gap-4 max-w-[1440px] mx-auto h-[440px] md:h-[480px]">
      {industries.map((item, i) => {
        const isActive = i === activeIndex;

        return (
          <div
            key={i}
            role={!isActive ? 'button' : undefined}
            tabIndex={!isActive ? 0 : undefined}
            onClick={() => !isActive && setActiveIndex(i)}
            onKeyDown={(e) => {
              if (!isActive && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                setActiveIndex(i);
              }
            }}
            className="relative overflow-hidden rounded-[48px] h-full transition-all duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{
              flexGrow: isActive ? 1 : 0,
              flexShrink: 0,
              flexBasis: isActive ? '0%' : '90px',
              cursor: isActive ? 'default' : 'pointer',
            }}
            aria-label={!isActive ? `View ${item.industry_name}` : undefined}
          >
            {/* Background image (shared across both states) */}
            {isFilled.image(item.background_image) && (
              <div className="absolute inset-0">
                <PrismicNextImage
                  field={item.background_image}
                  fill
                  className="object-cover"
                  sizes="(min-width: 1440px) 1440px, 100vw"
                  fallbackAlt=""
                />
                <div
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    isActive
                      ? 'bg-[linear-gradient(to_right,rgba(0,0,0,0)_0%,rgba(0,0,0,0)_9%,rgba(0,0,0,0.68)_13%,rgba(0,0,0,0.68)_100%)]'
                      : 'bg-black/65'
                  }`}
                />
              </div>
            )}

            {/* ACTIVE (expanded) CONTENT */}
            <div
              className="absolute inset-0 z-10 flex flex-col text-white py-6 md:py-10 transition-opacity duration-300"
              style={{
                opacity: isActive ? 1 : 0,
                transitionDelay: isActive ? '250ms' : '0ms',
                pointerEvents: isActive ? 'auto' : 'none',
              }}
            >
              <div className="flex items-center justify-center gap-3 pl-[13%] pr-6 md:pr-10">
                <span className="text-[12px] md:text-[13px] font-semibold uppercase tracking-[0.15em] whitespace-nowrap">
                  {item.industry_name}
                </span>
                <span className="w-3 h-3 rounded-full border border-white/70 shrink-0" />
              </div>

              <div className="mt-auto flex flex-col gap-6 md:gap-8 pl-[18%] pr-6 md:pr-10">
                <div className="flex flex-col gap-5 max-w-[440px]">
                  {isFilled.image(item.brand_logo) && (
                    <PrismicNextImage
                      field={item.brand_logo}
                      className="h-10 md:h-12 w-auto object-contain self-start"
                      fallbackAlt=""
                    />
                  )}

                  {item.stat_headline && (
                    <p className="text-white text-[20px] md:text-[24px] font-bold leading-[1.25]">
                      {item.stat_headline}
                    </p>
                  )}

                  {item.description && (
                    <div className="text-white text-[16px] md:text-[18px] leading-[1.45] font-semibold [&_p]:m-0">
                      <PrismicRichText field={item.description} />
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-5 md:gap-6 flex-wrap">
                  <GreenCtaLink
                    field={
                      isFilled.link(item.cta_link) ? item.cta_link : undefined
                    }
                    label={item.cta_label || 'View case study'}
                  />

                  {total > 1 && (
                    <>
                      <div className="flex-1 min-w-[40px] max-w-[120px] h-px bg-white/40" />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          aria-label="Previous"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrev();
                          }}
                          className="w-9 h-9 rounded-full border border-white/60 flex items-center justify-center hover:bg-white/10 transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4 text-white" />
                        </button>
                        <button
                          type="button"
                          aria-label="Next"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNext();
                          }}
                          className="w-9 h-9 rounded-full border border-white/60 flex items-center justify-center hover:bg-white/10 transition-colors"
                        >
                          <ChevronRight className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* INACTIVE (collapsed) CONTENT */}
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-between py-6 px-2 transition-opacity duration-300"
              style={{
                opacity: isActive ? 0 : 1,
                transitionDelay: !isActive ? '250ms' : '0ms',
                pointerEvents: isActive ? 'none' : 'auto',
              }}
            >
              <span className="w-3 h-3 rounded-full border border-white/70" />
              <span className="text-white text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.15em] [writing-mode:vertical-rl] rotate-180 whitespace-nowrap">
                {item.industry_name}
              </span>
              <span className="w-9 h-9 rounded-full border border-white/60 flex items-center justify-center">
                <Plus className="w-4 h-4 text-white" />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export type CaseIndustryShowcaseProps =
  SliceComponentProps<Content.CaseIndustryShowcaseSlice>;

const CaseIndustryShowcase: FC<CaseIndustryShowcaseProps> = ({
  slice,
  ...rest
}) => {
  if (slice.variation === 'redesignLogoCards') {
    return <CaseIndustryShowcaseRedesign slice={slice} {...rest} />;
  }
  return (
    <CaseIndustryShowcaseCurrent slice={slice as CaseIndustryShowcaseSlice} />
  );
};

const CaseIndustryShowcaseCurrent: FC<{ slice: CaseIndustryShowcaseSlice }> = ({
  slice,
}) => {
  const industries = slice.primary.industries || [];
  const [activeIndex, setActiveIndex] = useState(0);

  if (industries.length === 0) {
    return null;
  }

  // Variation with a centered header (kicker + title) above the carousel and a
  // "See all case studies" link below it. Cards are styled to match
  // IndustryTestimonialsCarousel/with_case_study_slider.
  if (slice.variation === 'measurableOutcomes') {
    const { kicker, title, see_all_label, see_all_link } = slice.primary;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="bg-[#FAFAFA] font-inter py-16 md:py-20"
      >
        {/* Inset content with the same side margin as HeroVelxEngine (mx-[30px]). */}
        <div className="mx-[30px]">
          <div className="mx-auto mb-10 flex max-w-[860px] flex-col items-center gap-5 text-center md:mb-14">
            {kicker && (
              <span className="rounded-full border border-[#7c5cff] px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[#7c5cff] md:text-[12px]">
                {kicker}
              </span>
            )}
            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="w-full text-balance text-[28px] font-semibold leading-[1.2] text-[#041018] md:text-[42px]">
                      {children}
                    </h2>
                  ),
                }}
              />
            )}
          </div>

          <SliderCarousel
            industries={industries}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />

          {isFilled.link(see_all_link) && (
            <div className="mt-10 flex justify-center md:mt-12">
              <PrismicNextLink
                field={see_all_link}
                className="inline-flex items-center gap-2 rounded-full border border-[#041018]/30 px-6 py-3 text-[14px] font-semibold text-[#041018] transition-colors hover:bg-black/5"
              >
                {see_all_label || 'See all case studies'}{' '}
                <span aria-hidden>→</span>
              </PrismicNextLink>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Static grid of case-study cards (no carousel): top image + badge, then a
  // dark panel with category, brand, stat headline, description and a link.
  if (slice.variation === 'proofGrid') {
    const { kicker, title, see_all_label, see_all_link } = slice.primary;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="bg-[#FAFAFA] font-inter py-16 md:py-20"
      >
        <div className="mx-[30px]">
          <div className="mx-auto mb-10 flex max-w-[860px] flex-col items-center gap-5 text-center md:mb-14">
            {kicker && (
              <span className="rounded-full border border-[#7c5cff] px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[#7c5cff] md:text-[12px]">
                {kicker}
              </span>
            )}
            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="w-full text-balance text-[28px] font-semibold leading-[1.2] text-[#041018] md:text-[42px]">
                      {children}
                    </h2>
                  ),
                }}
              />
            )}
          </div>

          <div className="mx-auto grid max-w-[680px] grid-cols-1 gap-5 sm:grid-cols-2">
            {industries.map((item, i) => (
              <article
                key={i}
                className="flex flex-col overflow-hidden rounded-[20px]"
              >
                {/* Top image + badge */}
                <div className="relative h-[200px] w-full">
                  {isFilled.image(item.background_image) && (
                    <PrismicNextImage
                      field={item.background_image}
                      fill
                      className="object-cover"
                      sizes="(min-width: 640px) 380px, 100vw"
                      fallbackAlt=""
                    />
                  )}
                  <span className="absolute left-5 top-5 rounded-full bg-white/15 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur-md">
                    {item.badge_label || 'Case study'}
                  </span>
                </div>

                {/* Dark panel */}
                <div className="flex flex-1 flex-col gap-3 bg-[linear-gradient(180deg,#0a0e13_0%,#0a0e13_50%,#2d1b69_100%)] p-5">
                  {item.industry_name && (
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-[2px] bg-[#00f557]" />
                      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70">
                        {item.industry_name}
                      </span>
                    </div>
                  )}

                  {isFilled.image(item.brand_logo) ? (
                    <PrismicNextImage
                      field={item.brand_logo}
                      className="h-6 w-auto max-w-[120px] self-start object-contain"
                      fallbackAlt=""
                    />
                  ) : (
                    item.brand_name && (
                      <p className="text-[16px] font-bold uppercase leading-[1.2] text-white">
                        {item.brand_name}
                      </p>
                    )
                  )}

                  {item.stat_headline && (
                    <p className="text-[14px] font-bold leading-[1.35] text-white">
                      {item.stat_headline}
                    </p>
                  )}

                  {isFilled.richText(item.description) && (
                    <div className="text-[13px] font-light leading-[1.55] text-white/70 [&_p]:m-0 [&_p+p]:mt-2">
                      <PrismicRichText field={item.description} />
                    </div>
                  )}

                  {isFilled.link(item.cta_link) && (
                    <PrismicNextLink
                      field={item.cta_link}
                      className="mt-auto self-start pt-2 text-[13px] font-semibold text-white underline decoration-[#00f557] decoration-2 underline-offset-4 transition-opacity hover:opacity-80"
                    >
                      {item.cta_label || 'View case study'}
                    </PrismicNextLink>
                  )}
                </div>
              </article>
            ))}
          </div>

          {isFilled.link(see_all_link) && (
            <div className="mt-10 flex justify-center md:mt-12">
              <PrismicNextLink
                field={see_all_link}
                className="inline-flex items-center gap-2 rounded-full border border-[#041018]/30 px-6 py-3 text-[14px] font-semibold text-[#041018] transition-colors hover:bg-black/5"
              >
                {see_all_label || 'See all case studies'}{' '}
                <span aria-hidden>→</span>
              </PrismicNextLink>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#FAFAFA] font-inter py-10 px-5 md:px-8 lg:px-[30px]"
    >
      <IndustryCarousel
        industries={industries}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
      />
    </section>
  );
};

export default CaseIndustryShowcase;
